<template>
  <div
    :class="rootClasses"
    :role="variantRole"
    :aria-live="ariaLive"
    data-cy="ds-alert"
    @mouseenter="dismiss.pause()"
    @mouseleave="dismiss.resume()"
  >
    <div :class="DS_ALERT_CLASSES.IconWrapper">
      <DSIcon
        :icon="iconName"
        size="sm"
        appearance="filled"
        data-cy="ds-alert__icon"
      />
    </div>

    <DSButton
      v-if="props.closeable"
      variant="ghost"
      size="sm"
      :aria-label="ARIA_LABEL_CLOSE"
      prefix-icon="cross"
      :class="DS_ALERT_CLASSES.CloseButton"
      data-cy="ds-alert__close-button"
      @click="handleDismiss"
    />

    <div :class="DS_ALERT_CLASSES.Content">
      <header
        v-if="props.header"
        :class="DS_ALERT_CLASSES.Header"
      >
        <div
          :class="DS_ALERT_CLASSES.Title"
          data-cy="ds-alert__title"
        >
          {{ props.header }}
        </div>
      </header>

      <component
        :is="hasDefaultSlot ? 'div' : 'p'"
        ref="bodyRef"
        :class="bodyClasses"
        data-cy="ds-alert__body"
      >
        <slot v-if="hasDefaultSlot" />
        <template v-else>{{ props.body }}</template>
      </component>

      <div
        v-if="showToggle"
        :class="DS_ALERT_CLASSES.Toggle"
        data-cy="ds-alert__toggle"
      >
        <DSButton
          variant="ghost"
          size="sm"
          :class="DS_ALERT_CLASSES.ToggleButton"
          data-cy="ds-alert__toggle-button"
          @click="toggleBody"
        >
          {{ toggleLabel }}
        </DSButton>
      </div>
    </div>

    <footer
      v-if="hasActions"
      :class="DS_ALERT_CLASSES.Footer"
      data-cy="ds-alert__footer"
    >
      <DSButton
        v-for="action in props.actions"
        :key="action.name"
        variant="secondary"
        size="sm"
        :href="action.href"
        :target="action.target"
        :class="DS_ALERT_CLASSES.ActionButton"
        data-cy="ds-alert__action-button"
        @click="handleActionClick(action)"
      >
        {{ action.title }}
      </DSButton>
    </footer>

    <DSProgressBar
      v-if="props.autoDismiss"
      :value="dismiss.progress.value"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useSlots } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import DSProgressBar from "@/src/design_system/feedback/progress_bar/components/DSProgressBar.vue";
import { DS_ALERT_CLASSES, DS_ALERT_VARIANT_TO_CLASS_MAP } from "@/src/design_system/feedback/alert/constants/classes";
import { DS_ALERT_VARIANT_TO_ICON_MAP } from "@/src/design_system/feedback/alert/constants/icons";
import { useAutoDismiss } from "@/src/design_system/feedback/alert/composables/useAutoDismiss";
import { useCollapsibleDescription } from "@/src/design_system/feedback/alert/composables/useCollapsibleDescription";
import { DS_ALERT_VARIANT, DSAlertVariant } from "@/src/design_system/feedback/alert/types/variant";
import type { DSAlertProps } from "@/src/design_system/feedback/alert/types/props";
import { DEFAULT_DISMISS_TIMEOUT_MS } from "@/src/design_system/feedback/alert/constants/dismiss";
import { DSAlertEmits } from "@/src/design_system/feedback/alert/types/emits";
import type { DSAlertAction } from "@/src/design_system/feedback/alert/types/action";

const BUTTON_SHOW_MORE_LABEL = window.I18n.designSystem.alert.buttonMore;
const BUTTON_SHOW_LESS_LABEL = window.I18n.designSystem.alert.buttonLess;
const ARIA_LABEL_CLOSE = window.I18n.designSystem.alert.ariaLabelClose;

const props = withDefaults(defineProps<DSAlertProps>(), {
  variant: DS_ALERT_VARIANT.Info as DSAlertVariant,
  closeable: true,
  dismissTimeoutMs: DEFAULT_DISMISS_TIMEOUT_MS,
  autoDismiss: false,
});

const emit = defineEmits<DSAlertEmits>();
const slots = useSlots();

const bodyRef = ref<HTMLElement | null>(null);

const dismissTimeout = computed(() => props.dismissTimeoutMs ?? DEFAULT_DISMISS_TIMEOUT_MS);

const dismiss = useAutoDismiss({
  dismissEnabled: props.autoDismiss,
  duration: dismissTimeout,
  onTimeout: () => emit("dismiss"),
});

const { isExpanded, canToggle, toggle } = useCollapsibleDescription({
  contentRef: bodyRef,
});

const hasDefaultSlot = computed(() => Boolean(slots.default));

const showToggle = computed(() => !hasDefaultSlot.value && canToggle.value);

const bodyClasses = computed(() => [
  DS_ALERT_CLASSES.Body,
  {
    [DS_ALERT_CLASSES.BodyExpanded]: isExpanded.value || hasDefaultSlot.value,
    [DS_ALERT_CLASSES.BodySlotted]: hasDefaultSlot.value,
  },
]);

const toggleLabel = computed(() => (isExpanded.value ? BUTTON_SHOW_LESS_LABEL : BUTTON_SHOW_MORE_LABEL));

const toggleBody = () => {
  toggle();
};

const handleDismiss = () => {
  dismiss.stop();
  emit("dismiss");
};

const handleActionClick = (action: DSAlertAction) => {
  if (!action.href) {
    emit("action", action.name);
  }
};

onMounted(() => {
  dismiss.start();
});

const rootClasses = computed(() => [DS_ALERT_CLASSES.Root, DS_ALERT_VARIANT_TO_CLASS_MAP[props.variant]]);

const DS_ALERT_VARIANT_TO_ROLE_MAP: Record<DSAlertVariant, "alert" | "status"> = {
  [DS_ALERT_VARIANT.Info]: "status",
  [DS_ALERT_VARIANT.Success]: "status",
  [DS_ALERT_VARIANT.Warning]: "alert",
  [DS_ALERT_VARIANT.Error]: "alert",
};

const variantRole = computed(() => DS_ALERT_VARIANT_TO_ROLE_MAP[props.variant]);
const ariaLive = computed(() => (variantRole.value === "alert" ? "assertive" : "polite"));

const hasActions = computed(() => Array.isArray(props.actions) && props.actions.length > 0);

const iconName = computed(() => DS_ALERT_VARIANT_TO_ICON_MAP[props.variant]);
</script>
